import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The label for an icon-only rail control, portalled out of the rail.
 *
 * It CANNOT be an absolutely-positioned child of the control, which is what it
 * was: the sidebar is `overflow-y-auto` so the nav can scroll, and an overflow
 * container clips both axes — so a tooltip at `left-full` was sliced off at the
 * rail's right edge and showed as a 2px white sliver. Exactly the trap
 * `useAnchoredMenu` exists for elsewhere in this app.
 *
 * Fixed coordinates measured from the trigger, rendered at `document.body`, so no
 * ancestor's overflow or stacking context can reach it.
 *
 * Returns handlers to spread on the trigger plus the tooltip node to render. It
 * is a hook rather than a wrapper component because the trigger is a `NavLink`
 * whose own props and render-prop children have to stay untouched.
 */
export function useRailTooltip(
  label: string,
  enabled: boolean,
): { anchorProps: Record<string, unknown>; tooltip: ReactNode } {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)

  const show = useCallback((event: { currentTarget: EventTarget | null }) => {
    const element = event.currentTarget as HTMLElement | null
    if (!element) return
    const rect = element.getBoundingClientRect()
    // Vertically centred on the trigger, but horizontally clear of the RAIL, not
    // of the trigger: a collapsed nav link is only 40px wide inside a 72px rail,
    // so 8px past its own right edge still lands on top of the sidebar.
    const rail = element.closest('aside')?.getBoundingClientRect()
    setAt({ top: rect.top + rect.height / 2, left: (rail?.right ?? rect.right) + 8 })
  }, [])

  const hide = useCallback(() => setAt(null), [])

  // A tooltip pinned to viewport coordinates goes stale the moment anything
  // scrolls — including the rail itself, which is exactly what the user is doing
  // when they reach the bottom of the nav. Dismiss rather than chase.
  useEffect(() => {
    if (at === null) return
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [at, hide])

  if (!enabled) return { anchorProps: {}, tooltip: null }

  return {
    anchorProps: {
      onPointerEnter: show,
      onPointerLeave: hide,
      // Keyboard users reach these too, and an icon with no visible name is not a
      // destination anyone can identify.
      onFocus: show,
      onBlur: hide,
    },
    tooltip:
      at === null
        ? null
        : createPortal(
            <span
              role="tooltip"
              style={{ top: at.top, left: at.left }}
              className="pointer-events-none fixed z-60 -translate-y-1/2 whitespace-nowrap rounded-control border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-lg"
            >
              {label}
            </span>,
            document.body,
          ),
  }
}
